import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesUp,
  faAnglesDown,
  faLock,
  faUnlock,
  faPlusSquare,
  faMinusSquare,
} from "@fortawesome/free-solid-svg-icons";
import useUSQS from "../../hooks/useUSQS";
import useUSQSUndo from "../../hooks/useUSQSUndo";
import QSUSAGeneral from "./QSUSAGeneral";
import QSUSAPackaging from "./QSUSAPackaging";
import "./QSUSA.css";
import QSUSAInCharge from "./QSUSAInCharge";
import QSUSADelivery from "./QSUSADelivery";
import QSUSAFigures from "./QSUSAFigures";
import QSUSAPayment from "./QSUSAPayment";
import { ignoreEnter, handleFigureChange, handleFigureBlur } from "./utils/inputhandlers";
import { resetCustoms, resetInbound, resetOceanfreight, resetOutbound } from "./utils/resetFunctions";
import QSUSAEcon from "./QSUSAEcon";
import useCalculations from "./hooks/useCalculations";
import * as Calculations from "./utils/calculations";
import useGlobalParams from "../../hooks/useGlobalParams";
import { undoLastEdit, redoLastEdit } from "./utils/undo";
import { saveNewUSQS, saveEdits } from "./api/APICalls";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useLoadQSs from "./hooks/useLoadQSs";
import ListWithFiltersQS from "../ListWithFiltersQS/ListWithFiltersQS";
import useLoadQSToEdit from "./hooks/useLoadQSToEdit";
import { offerToClipboard } from "./utils/offergenerators";
import useTransformCurrency from "./hooks/useTransformCurrency";

const QSUSA = () => {
  // General
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  // Load Context Values
  const { globalparams } = useGlobalParams();
  const {
    setQstoedit,
    qstoedit,
    USQS,
    USQSData,
    setUSQS,
    setUSQSData,
    USQSInit,
    USQSDataInit,
    ppaytermInitUSQS,
    ppaytermDataInitUSQS,
    spaytermInitUSQS,
    spaytermDataInitUSQS,
    setPpaymenttermUSQS,
    ppaymenttermUSQS,
    setPpaymenttermDataUSQS,
    ppaymenttermDataUSQS,
    ppayrowUSQS,
    setSpaymenttermUSQS,
    spaymenttermUSQS,
    setSpaymenttermDataUSQS,
    spaymenttermDataUSQS,
    spayrowUSQS,
    inEuros,
    setInEuros,
    ERlocked,
    setERlocked,
    currencyChangeFlag,
  } = useUSQS();
  const {
    USQSEdits,
    editIDUSQS,
    setEditIDUSQS,
    undoListUSQS,
    showcalculatorUSQS,
    setShowcalculatorUSQS,
    undoFocusFlagUSQS,
    setUndoFocusFlagUSQS,
    inputRefsUSQS,
    setEditingFieldUSQS,
    editingFieldUSQS,
  } = useUSQSUndo();

  // Refs
  const QSUSAsearchRef = useRef();
  const SQSMRef = useRef();
  const loadedQS = useRef(USQS);
  const loadedQSData = useRef(USQSData);
  const loadedPpayterm = useRef(structuredClone(ppaytermInitUSQS));
  const loadedPpaytermData = useRef(structuredClone(ppaytermDataInitUSQS));
  const loadedSpayterm = useRef(structuredClone(spaytermInitUSQS));
  const loadedSpaytermData = useRef(structuredClone(spaytermDataInitUSQS));

  // States
  const [SQSM, setSQSM] = useState(); //Save/Success/Failure Message
  const [showSQSM, setShowSQSM] = useState(false); // Flag to show SPM
  const [toggleClearSF, setToggleClearSF] = useState(false); //SearchField Clear
  const [showinfo, setShowinfo] = useState(); // Show Incoterm Info
  const [loaded, setLoaded] = useState(false); // Flag to know when the position data is loaded
  const [updateLoadedPayterms, setUpdatedLoadedPayterms] = useState(false); // Flag to know when the payterms are loaded
  const [reloadlistdata, setReloadlistdata] = useState(); // Flag to reload list data
  const [showQSUSASearch, setShowQSUSASearch] = useState(false); // Flag to show/hide QS list
  const [limit, setLimit] = useState(300); // sets row limit to load in List with Filters
  const [clearSelectedRowID, setClearSelectedRowID] = useState(); // clear selected row in List with Filters
  const [navpostoedit, setNavpostoedit] = useState(); // passed to QSUSAGeneral to set the loaded position
  const [showUSposList, setShowUSposList] = useState(false); // flag to hide/show poslist in QS
  const [reloadPoslistdata, setReloadPoslistdata] = useState(false); // flag to reload position list data

  // Set Insurance Variables when Incoterm changes
  useEffect(() => {
    USQS && Calculations.setInsuranceVars(USQS.sincoterm, globalparams, setUSQS, setUSQSData);
  }, [USQS.sincoterm]);

  //Calculations Hook
  useCalculations();

  // Tranform currency hook
  useTransformCurrency(USQSData, setUSQS, inEuros, USQS.exchangeRateUsdToEur, currencyChangeFlag);

  // Load List Data
  const listdata = useLoadQSs(axiosPrivate, reloadlistdata, navigate, location, "loadUSQSList", limit);

  // Store loaded position to compare edits against
  useEffect(() => {
    loadedQS.current = USQS;
    loadedQSData.current = USQSData;
  }, [loaded]);

  // Update paymentterm and paymenttermdata after loading to include a row value
  useEffect(() => {
    setPpaymenttermUSQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || ppayrowUSQS.current };
      })
    );
    setPpaymenttermDataUSQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || ppayrowUSQS.current };
      })
    );
    setSpaymenttermUSQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || spayrowUSQS.current };
      })
    );
    setSpaymenttermDataUSQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || spayrowUSQS.current };
      })
    );
    return setUpdatedLoadedPayterms((prev) => !prev);
  }, [loaded]);

  // Store loaded payment terms to compare edits against. Do this after update above.
  useEffect(() => {
    loadedPpayterm.current = structuredClone(ppaymenttermUSQS);
    loadedPpaytermData.current = structuredClone(ppaymenttermDataUSQS);
    ppayrowUSQS.current = 0;
    loadedSpayterm.current = structuredClone(spaymenttermUSQS);
    loadedSpaytermData.current = structuredClone(spaymenttermDataUSQS);
    spayrowUSQS.current = 0;
  }, [updateLoadedPayterms]);

  // Give focus to the undo input field using inputRefs
  useEffect(() => {
    if (undoFocusFlagUSQS && inputRefsUSQS.current[undoFocusFlagUSQS]) {
      inputRefsUSQS.current[undoFocusFlagUSQS].focus();
      setToggleClearSF((prev) => !prev);
    }
    return setUndoFocusFlagUSQS("");
  }, [undoFocusFlagUSQS]);

  // Attach Undo and Redo functions with addEventListener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for MetaKey+Z (Undo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        undoLastEdit(
          e,
          setToggleClearSF,
          USQSEdits,
          undoListUSQS,
          setUSQS,
          setUSQSData,
          setShowcalculatorUSQS,
          setUndoFocusFlagUSQS,
          loadedQS,
          loadedQSData,
          setEditIDUSQS,
          setPpaymenttermUSQS,
          setPpaymenttermDataUSQS,
          ppaymenttermUSQS,
          ppaymenttermDataUSQS,
          loadedPpayterm,
          loadedPpaytermData,
          setSpaymenttermUSQS,
          setSpaymenttermDataUSQS,
          spaymenttermUSQS,
          spaymenttermDataUSQS,
          loadedSpayterm,
          loadedSpaytermData,
          setNavpostoedit
        );
      }
      // Check for MetaKey+Shift+Z (Redo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && e.shiftKey) {
        redoLastEdit(
          e,
          setToggleClearSF,
          undoListUSQS,
          USQSEdits,
          setUSQS,
          setUSQSData,
          setShowcalculatorUSQS,
          setUndoFocusFlagUSQS,
          setEditIDUSQS,
          setPpaymenttermUSQS,
          setPpaymenttermDataUSQS,
          loadedPpayterm,
          loadedPpaytermData,
          setSpaymenttermUSQS,
          setSpaymenttermDataUSQS,
          loadedSpayterm,
          loadedSpaytermData,
          setNavpostoedit
        );
      }
    };
    // Add the event listener
    window.addEventListener("keydown", handleKeyDown);
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Show Saved Position Message
  const onSPMComplete = () => {
    setSQSM(null);
  };
  useEffect(() => {
    const elem = SQSMRef.current;
    if (SQSM) {
      gsap.fromTo(elem, { opacity: 0.5, top: "-50px" }, { opacity: 1, top: 0, duration: 0.1, ease: "power2.in" });
      gsap.fromTo(
        elem,
        { opacity: 1 },
        {
          opacity: 0,
          duration: 3,
          ease: "power2.inOut",
          onComplete: onSPMComplete,
        }
      );
    }
  }, [showSQSM]);

  // Show/Hide Position List Component
  useEffect(() => {
    if (showQSUSASearch) {
      const elem = QSUSAsearchRef.current;
      gsap.to(elem, {
        bottom: "-1vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
    if (!showQSUSASearch) {
      const elem = QSUSAsearchRef.current;
      gsap.to(elem, {
        bottom: "-73vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
  }, [showQSUSASearch]);

  // Clear QS Function
  const clearUSQS = () => {
    setUSQS(USQSInit);
    setUSQSData(USQSDataInit);
    setPpaymenttermUSQS(ppaytermInitUSQS);
    setPpaymenttermDataUSQS(ppaytermDataInitUSQS);
    setUSQSData(USQSDataInit);
    setSpaymenttermUSQS(spaytermInitUSQS);
    setSpaymenttermDataUSQS(spaytermDataInitUSQS);
    setQstoedit(null);
    setClearSelectedRowID(false);
    setLoaded((prev) => !prev);
    setUpdatedLoadedPayterms((prev) => !prev);
    setToggleClearSF((prev) => !toggleClearSF);
    setEditIDUSQS(0);
    setERlocked(false);
  };

  // Select Position to Edit
  const selectQSToEdit = (item) => {
    clearUSQS();
    setQstoedit(item.USQSID);
    setShowQSUSASearch(false);
  };

  // Load Position to Edit
  useLoadQSToEdit(
    axiosPrivate,
    qstoedit,
    setUSQS,
    setUSQSData,
    setPpaymenttermUSQS,
    setPpaymenttermDataUSQS,
    ppaytermInitUSQS,
    ppaytermDataInitUSQS,
    setLoaded,
    navigate,
    location,
    setSpaymenttermUSQS,
    setSpaymenttermDataUSQS,
    spaytermInitUSQS,
    spaytermDataInitUSQS,
    setShowSQSM,
    setSQSM,
    setNavpostoedit
  );

  return (
    <div className='addQSUSA-container'>
      <form
        className='addQSUSA'
        onSubmit={(e) => {
          e.preventDefault();
          if (qstoedit) {
            saveEdits(
              USQSEdits,
              axiosPrivate,
              qstoedit,
              setReloadlistdata,
              setSQSM,
              setShowSQSM,
              ppaymenttermDataUSQS,
              spaymenttermDataUSQS,
              setReloadPoslistdata
            );
          } else {
            saveNewUSQS(
              axiosPrivate,
              USQSData,
              ppaymenttermDataUSQS,
              setReloadlistdata,
              reloadlistdata,
              setSQSM,
              setShowSQSM,
              showSQSM,
              spaymenttermDataUSQS,
              clearUSQS,
              setReloadPoslistdata
            );
          }
        }}
        onClick={(e) => {
          setShowQSUSASearch(false);
          if (showUSposList) setShowUSposList(false);
          // setShowUSposList((prev) => !prev);
        }}
      >
        <div className='addQSUS-header'>
          <h1>{qstoedit ? "Edit USA QS:" : "Add New USA QS:"}</h1>
          <div className='USQS-success-msg' ref={SQSMRef}>
            {SQSM}
          </div>
          <div className='form-buttons'>
            <button
              type='button'
              tabIndex={201}
              onClick={(e) => {
                clearUSQS();
              }}
            >
              Clear
            </button>
            {!qstoedit && (
              <button className='mainbutton' type='submit' tabIndex={201}>
                Save New
              </button>
            )}
            {qstoedit && (
              <button
                className={USQSEdits.current.length > 0 ? "mainbutton" : "mainbutton btndisabled"}
                type='submit'
                tabIndex={201}
                // onClick={(e) => {

                // }}
              >
                Save Edits
              </button>
            )}
          </div>
        </div>
        <div className='addQSUSA-formcontainer'>
          <div className='addQSUSA-1'>
            <QSUSAGeneral
              clearfield={toggleClearSF}
              setClearfield={setToggleClearSF}
              navpostoedit={navpostoedit}
              setNavpostoedit={setNavpostoedit}
              showUSposList={showUSposList}
              setShowUSposList={setShowUSposList}
              setReloadPoslistdata={setReloadPoslistdata}
              reloadPoslistdata={reloadPoslistdata}
            />
            <QSUSAPayment
              loadedPpayterm={loadedPpayterm}
              loadedPpaytermData={loadedPpaytermData}
              loadedSpayterm={loadedSpayterm}
              loadedSpaytermData={loadedSpaytermData}
              ignoreEnter={(e) => ignoreEnter(e, setShowcalculatorUSQS, showcalculatorUSQS)}
              clearfield={toggleClearSF}
            />
          </div>
          <div className='addQSUSA-2' style={{ overflow: "visible" }}>
            <QSUSAInCharge clearfield={toggleClearSF} />
            <QSUSAPackaging clearfield={toggleClearSF} />
            <QSUSADelivery clearfield={toggleClearSF} setShowinfo={setShowinfo} showinfo={showinfo} />
          </div>
          <div className='addQSUSA-3'>
            <div className='addQSUSA-31'>
              <QSUSAFigures
                ignoreEnter={(e) => ignoreEnter(e, setShowcalculatorUSQS, showcalculatorUSQS)}
                setShowcalculator={setShowcalculatorUSQS}
                showcalculator={showcalculatorUSQS}
                resetOutbound={(e) =>
                  resetOutbound(
                    USQS,
                    USQSData,
                    setUSQS,
                    setUSQSData,
                    USQSInit,
                    USQSDataInit,
                    USQSEdits,
                    editIDUSQS,
                    setEditIDUSQS
                  )
                }
                resetOceanfreight={(e) => resetOceanfreight(setUSQS, setUSQSData, USQSInit, USQSDataInit)}
                resetCustoms={(e) => resetCustoms(setUSQS, setUSQSData, USQSInit, USQSDataInit)}
                resetInbound={(e) => resetInbound(setUSQS, setUSQSData, USQSInit, USQSDataInit)}
                clearfield={toggleClearSF}
              />
            </div>
            <div className='addQSUSA-32'>
              <QSUSAEcon clearfield={toggleClearSF} />
              <button
                id='copyOfferBtn'
                type='button'
                onClick={() => offerToClipboard(USQS, spaymenttermUSQS, setSQSM, setShowSQSM)}
              >
                Copy Offer to Clipboard
              </button>
              <div className='USQSsoldgroup'>
                <input
                  type='checkbox'
                  checked={USQS.sold === 1}
                  onChange={(e) => {
                    const newValue = e.target.checked ? 1 : 0;
                    setUSQS((prev) => ({ ...prev, sold: newValue }));
                    setUSQSData((prev) => ({ ...prev, sold: newValue }));
                    USQSEdits.current.push({
                      editID: editIDUSQS,
                      name: "sold",
                      value: newValue,
                      num: newValue,
                      figuretype: "radio",
                    });
                    setEditIDUSQS((prev) => prev + 1);
                    undoListUSQS.current = [];
                  }}
                />
                <label htmlFor=''>Sold:</label>
              </div>
              <div className='USQSsoldgroup' style={{ borderBottom: "1px dashed var(--blue)" }}>
                <input
                  style={{ marginTop: "1rem", marginBottom: "1.5rem" }}
                  type='checkbox'
                  checked={USQS.finalized === 1}
                  onChange={(e) => {
                    const newValue = e.target.checked ? 1 : 0;
                    setUSQS((prev) => ({ ...prev, finalized: newValue }));
                    setUSQSData((prev) => ({ ...prev, finalized: newValue }));
                    USQSEdits.current.push({
                      editID: editIDUSQS,
                      name: "finalized",
                      value: newValue,
                      num: newValue,
                      figuretype: "radio",
                    });
                    setEditIDUSQS((prev) => prev + 1);
                    undoListUSQS.current = [];
                  }}
                />
                <label style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>Finalized:</label>
              </div>
              <div className='form-group' style={{ marginTop: "1.5rem" }}>
                <label htmlFor=''>Exchange Rate (EUR/$):</label>
                <input
                  type='decimal'
                  autoComplete='off'
                  onKeyDown={ignoreEnter}
                  value={USQS?.["exchangeRateUsdToEur"] || ""}
                  name='exchangeRateUsdToEur'
                  onChange={(e) => handleFigureChange(e, "decimal", setUSQS, setUSQSData, setEditingFieldUSQS)}
                  onBlur={(e) =>
                    handleFigureBlur(
                      e,
                      "decimal",
                      setUSQS,
                      USQSEdits,
                      editIDUSQS,
                      setEditIDUSQS,
                      editingFieldUSQS,
                      setEditingFieldUSQS,
                      undoListUSQS,
                      undefined,
                      undefined,
                      4
                    )
                  }
                  ref={(el) => (inputRefsUSQS.current["exchangeRateUsdToEur"] = el)}
                  readOnly={ERlocked}
                />
                <FontAwesomeIcon icon={ERlocked ? faLock : faUnlock} onClick={() => setERlocked(true)} />
              </div>
              {ERlocked && (
                <button type='button' onClick={() => setInEuros((prev) => !prev)}>
                  {inEuros ? "Change to USD" : "Change to EUR"}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
      <div className='QSsearchUSA' ref={QSUSAsearchRef} tabIndex={-1}>
        <div className='QSsearchUSA-header' onClick={(e) => setShowQSUSASearch(!showQSUSASearch)}>
          {!showQSUSASearch ? <FontAwesomeIcon icon={faAnglesUp} /> : <FontAwesomeIcon icon={faAnglesDown} />}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <ListWithFiltersQS
            sortable={true}
            sortDirections={[
              "NON",
              "DESC",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              // "NON",
              "NON",
              "NON",
            ]}
            data={listdata}
            columns={[
              "QSDate",
              "USQSID",
              "QSType",
              "WGP",
              "WGS",
              "product",
              "supplier",
              "customer",
              "shipfrom",
              "shipuntil",
              "POL",
              "POD",
              "quantity",
              "cost",
              "freight",
              "salesprice",
              "profit",
              // "totalprofit",
              "margin",
              "sold",
            ]}
            size={["M", "S", "S", "S", "S", "M", "M", "M", "S", "S", "M", "M", "S", "M", "S", "M", "M", "S", "M"]}
            rowID={"USQSID"}
            placeHolders={true}
            placeHolderList={[
              "QSDate",
              "USQSID",
              "QSType",
              "WGP",
              "WGS",
              "Product",
              "Supplier",
              "Customer",
              "From",
              "To",
              "POL",
              "POD",
              "Quantity",
              "Cost",
              "Freight",
              "Price",
              "Profit",
              // "Total Profit",
              "Margin",
              "Sold",
            ]}
            limit={limit}
            setLimit={setLimit}
            setReloadlistdata={setReloadlistdata}
            onRowClick={selectQSToEdit}
            setClearSelectedRowID={setClearSelectedRowID}
            clearSelectedRowID={clearSelectedRowID}
            duplicateurl='duplicateUSQS'
            tabIndex={-1}
          />
        </div>
      </div>
    </div>
  );
};

export default QSUSA;
